'use client'

import { storyblokEditable, type SbBlokData } from '@storyblok/react'
import { xStack } from 'ui/src/patterns/stack'
import * as GridLayout from '@/components/GridLayout/GridLayout'
import type { Item as StoryCarouselItem, ImageAsset, VideoAsset } from '@/components/StoryCarousel/StoryCarousel.types'
import { StoryCarouselDesktop } from '@/components/StoryCarousel/StoryCarouselDesktop'
import { StoryCarouselMobile } from '@/components/StoryCarousel/StoryCarouselMobile'
import type { StoryblokAsset, SbBaseBlockProps, StoryblokTagsPlugin } from '@/services/storyblok/storyblok'
import { useBreakpoint } from '@/utils/useBreakpoint/useBreakpoint'

const IMAGE_ITEM_BLOCK_NAME = 'storyCarouselImageItem'
const VIDEO_ITEM_BLOCK_NAME = 'storyCarouselVideoItem'

type StoryCarouselImageItemBlock = SbBlokData & {
  asset: StoryblokAsset
  title?: string
  subtitle?: string
  tags?: StoryblokTagsPlugin
}

type StoryCarouselVideoItemBlock = SbBlokData & {
  videoUrlWebm: string
  title?: string
  subtitle?: string
  videoUrl?: string
  poster?: StoryblokAsset
  tags?: StoryblokTagsPlugin | Array<string>
  loop?: boolean
}

type StoryCarouselBlockProps = SbBaseBlockProps<{
  items: Array<StoryCarouselImageItemBlock | StoryCarouselVideoItemBlock>
}>

export function StoryCarouselBlock({ blok }: StoryCarouselBlockProps) {
  const isDesktop = useBreakpoint('md')

  const items = blok.items
    .filter((item) => isStoryCarouselImageItem(item) || isStoryCarouselVideoItem(item))
    .map(carouselItemBlockToStoryCarouselItem)

  if (items.length === 0) {
    console.log('[StoryCarouselBlock] No items found in block. Skipping rendering.')
    return null
  }

  if (isDesktop) {
    return <StoryCarouselDesktop items={items} />
  }

  return (
    <GridLayout.Root key={blok._uid} {...storyblokEditable(blok)}>
      <GridLayout.Content className={xStack({ justifyContent: 'center' })} width="1">
        <StoryCarouselMobile items={items} />
      </GridLayout.Content>
    </GridLayout.Root>
  )
}

function isStoryCarouselImageItem(
  blok: StoryCarouselImageItemBlock | StoryCarouselVideoItemBlock,
): blok is StoryCarouselImageItemBlock {
  return blok.component === IMAGE_ITEM_BLOCK_NAME
}

function isStoryCarouselVideoItem(
  blok: StoryCarouselImageItemBlock | StoryCarouselVideoItemBlock,
): blok is StoryCarouselVideoItemBlock {
  return blok.component === VIDEO_ITEM_BLOCK_NAME
}

function carouselItemBlockToStoryCarouselItem(
  blok: StoryCarouselImageItemBlock | StoryCarouselVideoItemBlock,
): StoryCarouselItem {
  let asset: ImageAsset | VideoAsset
  if (isStoryCarouselImageItem(blok)) {
    asset = {
      type: 'image',
      src: blok.asset.filename,
      alt: blok.asset.alt,
    }
  } else {
    const sources: Array<{ url: string }> = []
    // Order is important here; 'videoUrl' might contain MP4 video with alpha channel so it should came first
    const providedVideoUrls = [blok.videoUrl, blok.videoUrlWebm].filter(isValidUrl)
    providedVideoUrls.forEach((url) => sources.push({ url }))

    asset = {
      type: 'video',
      sources,
      poster: blok.poster?.filename,
      loop: blok.loop,
    }
  }

  return {
    id: blok._uid || crypto.randomUUID(),
    asset,
    title: blok.title,
    subtitle: blok.subtitle,
    // This is a temporary solution to support both 'StoryblokTagsPlugin' and array of strings; we don't control 'storyblok-tags' plugin and
    // we've experienced that sometimes it returns and array of strings instead of 'StoryblokTagsPlugin' type. We'll be ditching
    // away from it for obvious reasons but in the meantime I'm making sure to support both cases.
    tags: Array.isArray(blok.tags) ? blok.tags : blok.tags?.value,
  }
}

function isValidUrl(url?: string): url is string {
  return typeof url === 'string' && url.trim().length > 0
}
